/* eslint-disable max-len */
import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { ReferenceDatum } from '@sondermind/data-access/reference-data';
import { IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';
import { RadioOption } from '@sondermind/iris/radio-group';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';

import { IClientTerminationReason } from '../../models/client-termination-reasons.model';
import { ClientActivation, ClientTerminationReasonsModalService } from '../../services/client-termination-reasons/client-termination-reasons-modal.service';
import { ClientTerminationReasonsHttpService } from '../../services/client-termination-reasons/client-termination-reasons.httpservice';
/* eslint-enable max-len */

@Component({
  selector: 'client-termination-reasons-modal',
  templateUrl: './client-termination-reasons-modal.component.html',
  styleUrls: ['./client-termination-reasons-modal.component.scss'],
  standalone: false
})

export class ClientTerminationReasonsModalComponent implements OnInit, OnDestroy {
  @Input() displayName: string = 'client';

  modalVisible: boolean = false;
  submitting: boolean = false;
  clientTerminationReasonsForm: UntypedFormGroup;
  clientTerminationReasons: ReferenceDatum[];
  radioOptions: Array<RadioOption<number>>;
  isOtherSelected: boolean = false;
  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  private destroyed$ = new Subject<void>();
  IrisButtonTypes = IrisButtonTypes;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  constructor(
    private clientTerminationReasonsModalService: ClientTerminationReasonsModalService,
    private clientTerminationReasonsHttpService: ClientTerminationReasonsHttpService,
    private formBuilder: UntypedFormBuilder
  ) {}

  ngOnInit() {
    this.clientTerminationReasonsModalService.subscribeToModalEvents().pipe(
      takeUntil(this.destroyed$)
    ).subscribe((status) => {
      this.modalVisible = status.visibility;
    });

    this.clientTerminationReasonsForm = this.initializeClientTerminationReasonsForm();

    // Gather all termination reasons
    this.clientTerminationReasonsHttpService.terminationReasons$.pipe(
      take(1)
    ).subscribe((reasons) => {
      this.clientTerminationReasons = reasons;
      this.radioOptions = reasons.map((reason) => ({ name: reason.description, value: reason.id }));
      this.setClientTerminationFormValidators();
    });

    // Check for patient id, and if it exists assign to patient_id form control
    this.clientTerminationReasonsModalService.getSelectedPatientId().pipe(
      takeUntil(this.destroyed$)
    ).subscribe((id) => {
      if (id !== null) {
        this.clientTerminationReasonsForm.get('patient_id').setValue(id);
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  refIdCheck(id: number): ReferenceDatum {
    return this.clientTerminationReasons.find((ref: ReferenceDatum) => ref.id === id);
  }

  onSubmitClientTerminationReasonsForm() {
    this.submitting = true;
    const postBody: IClientTerminationReason = this.clientTerminationReasonsForm.value;

    this.clientTerminationReasonsHttpService.updateClientTerminationReason(postBody).subscribe({
      next: () => {
        this.submitting = false;
        this.handleCloseModal(true);
      },
      error: (err) => {
        this.submitting = false;
      }
    });
  }

  handleCloseModal(setInactive: boolean = false): void {
    this.clientTerminationReasonsModalService.toggleModal(
      false,
      setInactive ? ClientActivation.INACTIVE : null
    );
  }

  private initializeClientTerminationReasonsForm(): UntypedFormGroup {
    return this.formBuilder.group({
      reference_data_id: this.formBuilder.control(null, Validators.required),
      termination_info: this.formBuilder.control(null),
      patient_id: this.formBuilder.control(null),
    });
  }

  private setClientTerminationFormValidators(): void {
    const referenceDataIdControl: AbstractControl = this.clientTerminationReasonsForm.get('reference_data_id');
    const terminationInfoControl: AbstractControl = this.clientTerminationReasonsForm.get('termination_info');

    referenceDataIdControl.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((value) => {
      const selectedRefDataItem = this.refIdCheck(value);

      // Handle if "Other" option is selected.
      // If it is (aka not null), mark the textarea as required and show input
      if (!!selectedRefDataItem && selectedRefDataItem.tag !== null) {
        terminationInfoControl.setValidators([Validators.required]);
        this.isOtherSelected = true;
      }
      // Otherwise, remove validation from text area
      else {
        terminationInfoControl.setValidators(null);
        this.isOtherSelected = false;
      }

      terminationInfoControl.updateValueAndValidity();
      this.clientTerminationReasonsForm.updateValueAndValidity();
    });
  }
}
